import React, {useState, useEffect, useCallback, useRef} from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { createAuthAxios } from 'utils/authAxios';
import MyMap from 'pages/UnitsOverview/MyMap';
import ZeusChartTable from './ZeusChartTable.tsx'
import SessionOverview from './SessionOverview';
import LiveView from './LiveView';
import './UnitsOverview.scss';

import {
  createColumnHelper
} from '@tanstack/react-table'

// Data parsing
const getCpuChartData = (chartData) => {
  const obj = {}
  for (const [unit, unitData] of Object.entries(chartData)) {
    const events = unitData?.events
    if (events && events.length) {
      const timeArray = events.map(o => o.created_at).map((time) => new Date(time).getTime())
      const cpuArray = events.map(o => o.cpu).map((p) => Math.round(p * 10)/10)
      const graphData = timeArray.map((time, i) => ({ 'time': time, 'value': cpuArray[i]}) )
      obj[String(unit)] = graphData
    }
  }
  return obj
}

const newParse = (txt, unit) => {
  let a;
  try {
      a = JSON.parse(txt);
  } catch (e) {
      //console.log("Unable to retrieve data from unit" + unit + ":", '\n', e)
      return undefined;
  }
  return a;
}

const getDiskChartData = (chartData, erroredUnits, setErroredUnits, setDiskErrors) => {
  const obj = {}
  for (const [unit, unitData] of Object.entries(chartData)) {
    const events = unitData?.events
    if (events && events.length) {
      const timeArray = events.map(o => o.created_at).map((time) => new Date(time).getTime())
      const diskUsage = events.map(o => newParse(o.disk_usage, unit))
      if(diskUsage.includes(undefined)){
        setDiskErrors(prev => ({...prev, [unit]: true}))
        if(!erroredUnits.has(`disk-${unit}`)){
          //toast.error("Failed to get system disk information for unit " + unit)
          setErroredUnits(prev => new Set([...prev, `disk-${unit}`]))
        }
      }
      const diskArray = diskUsage.map(o => o?.disks[0]?.percent)
      const graphData = timeArray.map((time, i) => ({ 'time': time, 'value': diskArray[i]}) )
      obj[String(unit)] = graphData
    }
  }
  return obj
}

const getMemoryChartData = (chartData, erroredUnits, setErroredUnits) => {
  const obj = {}
  for (const [unit, unitData] of Object.entries(chartData)) {
    const events = unitData?.events
    if (events && events.length) {
      const timeArray = events.map(o => o.created_at).map((time) => new Date(time).getTime())
      const memoryInfo = events.map(o => newParse(o.memory_info, unit))
      if(memoryInfo.includes(undefined) && !erroredUnits.has(`memory-${unit}`)){
        //toast.error("Failed to get memory information for unit " + unit)
        setErroredUnits(prev => new Set([...prev, `memory-${unit}`]))
      }
      const memoryArray = memoryInfo.map(o => o?.percent)
      const graphData = timeArray.map((time, i) => ({ 'time': time, 'value': memoryArray[i]}) )
      obj[String(unit)] = graphData
    }
  }
  return obj
}

const getSpeedData = (chartData) => {
  const obj = {}
  for (const [unit, unitData] of Object.entries(chartData)) {
    const events = unitData?.events
    if (events && events.length) {
      const speedArray = events.map(o => o.speed)
      obj[String(unit)] = speedArray[0]
    }
  }
  return obj
}

const getBearingData = (chartData) => {
  const obj = {}
  for (const [unit, unitData] of Object.entries(chartData)) {
    const events = unitData?.events
    if (events && events.length) {
      const bearingArray = events.map(o => o.bearing)
      obj[String(unit)] = bearingArray[0]
    }
  }
  return obj
}

const getStateData = (chartData) => {
  const obj = {}
  for (const [unit, unitData] of Object.entries(chartData)) {
    const events = unitData?.events
    if (events && events.length) {
      const stateArray = events.map(o => o.state)
      obj[String(unit)] = stateArray[0]
    }
  }
  return obj
}

const fixDate = (rawDate) => {
  if (!rawDate) return 'No date'
  const date = new Date(rawDate)
  if (isNaN(date.getTime())) return 'Invalid date'
  const dateArray = date.toISOString().split('T')
  return dateArray[0] + " " + dateArray[1].split('.')[0]
}


export default function UnitsOverview() {

  const [cpuData, setCpuData] = useState({})
  const [diskData, setDiskData] = useState({})
  const [memoryData, setMemoryData] = useState({})
  const [speed, setSpeed] = useState({})
  const [bearing, setBearing] = useState({})
  const [state, setState] = useState({})

  const API = createAuthAxios();
  
  // Table data
  const [data, setData] = useState({})
  const [unitsData, setUnitsData] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [erroredUnits, setErroredUnits] = useState(new Set())
  const [diskErrors, setDiskErrors] = useState({})
  const [showRowDetails, setShowRowDetails] = useState(new Array(50).fill(false))
  const [selectedUnit, setSelectedUnit] = useState(null)
  const [prevMockPosition, setPrevMockPosition] = useState(null)

  // Mock moving position for unit 115 between Paris and Versailles at 500km/h
  const getMockPosition = () => {
    // Paris coordinates: 48.8566, 2.3522
    // Versailles coordinates: 48.8014, 2.1301
    const parisLat = 48.8566
    const parisLng = 2.3522
    const versaillesLat = 48.8014
    const versaillesLng = 2.1301
    
    // Distance between Paris and Versailles is approximately 20km
    // At 100km/h, it takes 12 minutes (720 seconds) one way
    // Round trip takes 24 minutes (1440 seconds)
    const roundTripTime = 1440 * 1000 // milliseconds
    const currentTime = Date.now() % roundTripTime
    const progress = currentTime / roundTripTime
    
    // Create smooth back-and-forth movement using sine wave
    const t = (Math.sin(progress * 2 * Math.PI) + 1) / 2 // Normalize to 0-1
    
    const currentLat = parseFloat((parisLat + (versaillesLat - parisLat) * t).toFixed(4))
    const currentLng = parseFloat((parisLng + (versaillesLng - parisLng) * t).toFixed(4))
    
    // Calculate bearing and speed based on movement from previous position
    let bearing = 0
    let speed = 0
    
    if (prevMockPosition) {
      const deltaLat = currentLat - prevMockPosition.lat
      const deltaLng = currentLng - prevMockPosition.lng
      const deltaTime = (currentTime - (prevMockPosition.timestamp || currentTime)) / 1000 // seconds
      
      if (deltaLat !== 0 || deltaLng !== 0) {
        // Calculate bearing in degrees (0° = North, 90° = East)
        bearing = (Math.atan2(deltaLng, deltaLat) * 180 / Math.PI + 360) % 360
        
        // Calculate distance using Haversine formula (approximate)
        const R = 6371000 // Earth's radius in meters
        const dLat = deltaLat * Math.PI / 180
        const dLng = deltaLng * Math.PI / 180
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) + 
                  Math.cos(prevMockPosition.lat * Math.PI / 180) * Math.cos(currentLat * Math.PI / 180) * 
                  Math.sin(dLng/2) * Math.sin(dLng/2)
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
        const distance = R * c // meters
        
        // Calculate speed in km/h
        if (deltaTime > 0) {
          speed = (distance / deltaTime) * 3.6 // m/s to km/h
        }
      } else {
        bearing = prevMockPosition.bearing || 0
        speed = prevMockPosition.speed || 0
      }
    }
    
    const position = {
      lat: currentLat,
      lng: currentLng,
      speed: Math.round(speed),
      bearing: bearing,
      timestamp: currentTime
    }
    
    setPrevMockPosition(position)
    return position
  }

  async function getUnits() {

    try {
      const resUsers = await API.post("/units/my-units/summary");
      let unitsData = resUsers.data;
      
      // Add mock position for unit 115 if it exists but has no position
      if (Array.isArray(unitsData)) {
        unitsData = unitsData.map(unit => {
          if (unit.unit_id === 115 && (!unit.last_pos_latitude || !unit.last_pos_longitude)) {
            const mockPos = getMockPosition()
            //console.log('Adding mock position for unit 115:', mockPos)
            return {
              ...unit,
              last_pos_latitude: mockPos.lat,
              last_pos_longitude: mockPos.lng
            }
          }
          return unit
        })
      }
      
      setData(unitsData);
    } catch (error) {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        // Don't show toast for auth errors, let interceptor handle redirect
        return;
      }
      console.log(error?.response?.data);
      toast.error(error?.response?.data)
    }
  }

  const isInitialLoadRef = useRef(true)
  
  const getUnitsData = useCallback(async () => {
    if (isLoading) {
      return;
    }

    try {
      setIsLoading(true)
      const resUnitData = await API.post("/units/my-units/data", {
        timeWindow: 15,
        initialLoad: isInitialLoadRef.current
      });
      
      if (resUnitData.data) {
        setUnitsData(resUnitData.data);
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
        }
      }
    } catch (error) {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        // Don't show toast for auth errors, let interceptor handle redirect
        return;
      }
      const errorMsg = error?.response?.data || error?.message || 'Failed to fetch units data';
      console.log('Error fetching units data:', errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    getUnitsData()
    
    const interval = setInterval(() => {
      getUnitsData()
    }, 2000)
    
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (unitsData && Object.keys(unitsData).length > 0) {
      // Process chart data - append new data to existing
      const cpu = getCpuChartData(unitsData)
      const disk = getDiskChartData(unitsData, erroredUnits, setErroredUnits, setDiskErrors)
      const memory = getMemoryChartData(unitsData, erroredUnits, setErroredUnits)
      const state = getStateData(unitsData)
      const speed = getSpeedData(unitsData)
      const bearing = getBearingData(unitsData)
      
      if (Object.keys(cpu).length > 0) {
        setCpuData(prev => {
          const updated = { ...prev }
          Object.keys(cpu).forEach(unit => {
            updated[unit] = [...(prev[unit] || []), ...cpu[unit]].slice(-50) // Keep last 50 points
          })
          return updated
        })
      }
      if (Object.keys(disk).length > 0) {
        setDiskData(prev => {
          const updated = { ...prev }
          Object.keys(disk).forEach(unit => {
            updated[unit] = [...(prev[unit] || []), ...disk[unit]].slice(-50)
          })
          return updated
        })
      }
      if (Object.keys(memory).length > 0) {
        setMemoryData(prev => {
          const updated = { ...prev }
          Object.keys(memory).forEach(unit => {
            updated[unit] = [...(prev[unit] || []), ...memory[unit]].slice(-50)
          })
          return updated
        })
      }
      if (Object.keys(state).length > 0) setState(state)
      // Add mock speed and bearing for unit 115
      if (unitsData['115']) {
        const mockPos = getMockPosition()
        speed['115'] = mockPos.speed
        bearing['115'] = mockPos.bearing
      }
      
      if (Object.keys(speed).length > 0) setSpeed(speed)
      if (Object.keys(bearing).length > 0) setBearing(bearing)
      
      // Process table data - include all units from unitsData
      const tableData = Object.entries(unitsData).map(([unitId, unitData]) => {
        const events = unitData.events || []
        const latestEvent = events[0]
        

        
        // Use event position if valid (not 0,0), otherwise use unit fallback position
        const eventLat = latestEvent?.position_latitude
        const eventLng = latestEvent?.position_longitude
        const useEventPosition = eventLat && eventLng && (eventLat !== 0 || eventLng !== 0)
        
        let processedUnit = {
          unit_id: parseInt(unitId),
          name: unitData.name,
          last_update: latestEvent?.created_at,
          last_pos_latitude: useEventPosition ? eventLat : unitData.last_pos_latitude,
          last_pos_longitude: useEventPosition ? eventLng : unitData.last_pos_longitude,
          bearing: bearing[unitId] || 0
        }
        

        
        // Add mock position and bearing for unit 115
        if (parseInt(unitId) === 115) {
          const mockPos = getMockPosition()
          processedUnit.last_update = new Date().toISOString()
          processedUnit.last_pos_latitude = mockPos.lat
          processedUnit.last_pos_longitude = mockPos.lng
          processedUnit.bearing = mockPos.bearing
        }
        
        return processedUnit
      })
      
      setData(tableData)
    }
  }, [unitsData])

  const actions = [
    {label: "Record", action: "record"},
    {label: "Remote Access", action: "access"}
  ]
  const unitAction = (action, unit_id) => {
    console.log("unitRecord has been activated", action, unit_id)
    if (action === 'play') {
      setSessionOverviewUnit(unit_id)
      setSessionOverviewOpen(true)
      setContextMenuStatus({show: false})
    } else {
      alert("Context menu action '" + action + "' for unit: " + unit_id)
    }
  }

  const [activeUnit, setActiveUnit] = useState();
  const [contextMenuStatus, setContextMenuStatus] = useState({show:false, x:0, y:0});
  const [sessionOverviewOpen, setSessionOverviewOpen] = useState(false);
  const [sessionOverviewUnit, setSessionOverviewUnit] = useState(null);
  const [liveStreamOpen, setLiveStreamOpen] = useState(false);
  const [liveStreamUnit, setLiveStreamUnit] = useState(null);
  const contextMenu = useRef(null)

  const openContextMenuHandler = useCallback(e => {
    e.preventDefault();
    let { pageX, pageY } = e;
    pageX = pageX - 280;
    pageY = pageY - 150;
    setContextMenuStatus({show:true, x:pageX, y:pageY})
}, []);

const hideContextMenu = (e) => {
  if (contextMenuStatus.show &&  !contextMenu.current.contains(e.target)) {
      setContextMenuStatus({show:false})
  }
}

document.onmousedown = (e) => hideContextMenu(e);

useEffect(() => {
  document.addEventListener('mousedown', hideContextMenu)
  return () => {
    document.removeEventListener('mousedown', hideContextMenu);
  };
}, [])

  const columnHelper = createColumnHelper()
  const columns = [
    columnHelper.accessor(row => row.unit_id, {
      id: 'ID',
      cell: ({ row }) => (
        <span>
          <i className={`fa ${showRowDetails[row.id] ? 'fa-square-minus' : 'fa-square-plus'}`} 
             style={{ color: '#238d5f', marginRight: '8px' }}></i>
          {row.original.unit_id}
        </span>
      ),
      header: () => <span>ID</span>,
      footer: info => info.column.id,
    }),
    columnHelper.accessor(row => row.name, {
      id: 'Name',
      cell: info => info.getValue(),
      header: () => <span>Name</span>,
      footer: info => info.column.id,
    }),
    columnHelper.accessor(row => row.last_update || '1900-01-01T00:00:00.000Z', {
      id: 'Updated',
      cell: info => <i>{fixDate(info.row.original.last_update)}</i>,
      header: () => <span>Updated</span>,
      footer: info => info.column.id,
      sortingFn: 'datetime',
    }),
    columnHelper.accessor(row => row.last_pos_latitude, {
      id: 'Position latitude',
      cell: info => <i>{info.getValue()}</i>,
      header: () => <span>Position latitude</span>,
      footer: info => info.column.id,
    }),
    columnHelper.accessor(row => row.last_pos_longitude, {
      id: 'Position longitude',
      cell: info => <i>{info.getValue()}</i>,
      header: () => <span>Position longitude</span>,
      footer: info => info.column.id,
    }),
    columnHelper.accessor(row => state[row.unit_id], {
      id: 'State',
      cell: info => <span>{info.getValue() || 'N/A'}</span>,
      header: () => <span>State</span>,
      footer: info => info.column.id,
    }),
  ]

    return (
      <div>
        <div>
          <Toaster
            position="top-right"
            reverseOrder={false}
          />
        </div>
        <div className="header pb-6">
          <div className="container-fluid">
            <div className="header-body">
              <div className="row align-items-center py-4">
                <div className="col-lg-6 col-7">
                  <h2 className="h2 d-inline-block mb-0">Units</h2>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="container-fluid mt--6">
          <div className="row">
            <div className="col">
              <div className="card border-0">
                <div id="container-map" className="map-canvas" style={{height: 'calc(100vh - 200px)', maxHeight: '669px', position: 'relative', overflow: 'hidden'}}>
                  <MyMap 
                  //contextAction={unitAction} 
                  openContextMenuHandler = {openContextMenuHandler}
                  setActiveUnit = {setActiveUnit}
                  //hideContextMenu = {hideContextMenu}
                  data={data}
                  stateData={state}
                  selectedUnit={selectedUnit}
                  setSelectedUnit={setSelectedUnit}
                  onSessionVideos={(unitId) => {
                    setSessionOverviewUnit(unitId)
                    setSessionOverviewOpen(true)
                  }}
                  onOpenUnit={(unitId) => {
                    setLiveStreamUnit(unitId)
                    setLiveStreamOpen(true)
                  }}
                  />
                  <ul id="contextMenu" ref={contextMenu} className={`context-menu-list context-menu-root ${contextMenuStatus.show ? '' : 'hidden'}`} style={{ width: 208 + "px", top: contextMenuStatus.y + "px", left: contextMenuStatus.x + "px", zIndex: "100001" }}>
                      {
                          actions.map(action => {
                              return (
                                  <li key={action.label} className="context-menu-item" onClick={() => unitAction(action.action,activeUnit)}><span>{action.label}</span></li>
                              )
                          })
                      }
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        { unitsData &&
          <ZeusChartTable
            columns={columns}
            data={data}
            chartData={unitsData}
            refreshData={getUnits}
            cpuData = {cpuData}
            diskData = {diskData}
            memoryData = {memoryData}
            speed = {speed}
            bearing = {bearing}
            diskErrors = {diskErrors}
            showRowDetails = {showRowDetails}
            setShowRowDetails = {setShowRowDetails}
            selectedUnit={selectedUnit}
            setSelectedUnit={setSelectedUnit}
          />
        }
        <SessionOverview
          isOpen={sessionOverviewOpen}
          onClose={() => setSessionOverviewOpen(false)}
          unitId={sessionOverviewUnit}
          unitName={sessionOverviewUnit && unitsData[sessionOverviewUnit] ? unitsData[sessionOverviewUnit].name : null}
        />
        <LiveView
          isOpen={liveStreamOpen}
          onClose={() => setLiveStreamOpen(false)}
          unitId={liveStreamUnit}
          unitName={liveStreamUnit && unitsData[liveStreamUnit] ? unitsData[liveStreamUnit].name : null}
        />
        
    </div>
    )
}